import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoryExplorer() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('');

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      setSelected('');
    }
  };

  return (
    <section id="browse" className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-2xl font-bold text-stone-900 md:text-3xl">
            {t('category.title')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-stone-500 md:text-base">
            {t('category.subtitle')}
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-md">
          <label
            htmlFor="category-select"
            className="mb-2 block text-sm font-medium text-stone-700"
          >
            {t('category.dropdownLabel')}
          </label>
          <div className="relative">
            <select
              id="category-select"
              value={selected}
              onChange={handleSelect}
              className="w-full appearance-none rounded-lg border border-stone-200 bg-cream px-4 py-3 pr-10 text-sm text-stone-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            >
              <option value="" disabled>
                {t('category.dropdownPlaceholder')}
              </option>
              {categories.map((cat) => (
                <option key={cat.key} value={cat.url}>
                  {t(`categories.${cat.key}`)}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
              <i className="ri-arrow-down-s-line text-base text-stone-500"></i>
            </span>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-stone-100 bg-white transition-all hover:-translate-y-1 hover:border-stone-200"
            >
              <div className="relative h-40 w-full overflow-hidden md:h-44">
                <img
                  src={cat.image}
                  alt={`${t(`categories.${cat.key}`)} collection`}
                  title={`${t(`categories.${cat.key}`)}`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-2 p-3">
                <span className="truncate text-sm font-semibold text-stone-800">
                  {t(`categories.${cat.key}`)}
                </span>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}